import { supabase } from '@/products/shared/services/supabase/client';
import {
  COMPLIANCE_DISCLAIMER_TABLE,
  COMPLIANCE_DISCLAIMER_VERSION,
} from '../constants/disclaimer';

export type ComplianceDisclaimerAcceptance = {
  id: number;
  tenant_id: number;
  user_id: string;
  disclaimer_version: string;
  accepted_at: string;
};

const acceptancesTable = () =>
  (supabase as any).from(COMPLIANCE_DISCLAIMER_TABLE);

const requireTenantId = (tenantId: number): void => {
  if (!Number.isInteger(tenantId) || tenantId <= 0) {
    throw new Error('A valid tenant is required to record disclaimer acceptance');
  }
};

const requireUserId = (userId: string): void => {
  if (!userId.trim()) {
    throw new Error('A valid user is required to record disclaimer acceptance');
  }
};

const requireVersion = (version: string): void => {
  if (!version.trim()) {
    throw new Error('A valid disclaimer version is required');
  }
};

const isUniqueViolation = (error: { code?: string; message?: string }): boolean =>
  error.code === '23505' ||
  Boolean(error.message?.toLowerCase().includes('duplicate'));

/**
 * Returns the acceptance row for the given version, or null if not yet accepted.
 */
export const fetchComplianceDisclaimerAcceptance = async (
  tenantId: number,
  userId: string,
  version: string = COMPLIANCE_DISCLAIMER_VERSION,
): Promise<ComplianceDisclaimerAcceptance | null> => {
  requireTenantId(tenantId);
  requireUserId(userId);
  requireVersion(version);

  const { data, error } = await acceptancesTable()
    .select('id, tenant_id, user_id, disclaimer_version, accepted_at')
    .eq('tenant_id', tenantId)
    .eq('user_id', userId)
    .eq('disclaimer_version', version)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return (data as ComplianceDisclaimerAcceptance | null) ?? null;
};

/**
 * Persists acceptance for the current disclaimer version.
 * Idempotent: a concurrent/duplicate insert returns the existing row.
 */
export const acceptComplianceDisclaimer = async (
  tenantId: number,
  userId: string,
  version: string = COMPLIANCE_DISCLAIMER_VERSION,
): Promise<ComplianceDisclaimerAcceptance> => {
  requireTenantId(tenantId);
  requireUserId(userId);
  requireVersion(version);

  const { data, error } = await acceptancesTable()
    .insert({
      tenant_id: tenantId,
      user_id: userId,
      disclaimer_version: version,
    })
    .select('id, tenant_id, user_id, disclaimer_version, accepted_at')
    .single();

  if (!error) {
    return data as ComplianceDisclaimerAcceptance;
  }

  if (!isUniqueViolation(error)) {
    throw error;
  }

  const existing = await fetchComplianceDisclaimerAcceptance(
    tenantId,
    userId,
    version,
  );
  if (!existing) {
    throw error;
  }
  return existing;
};
